/**
 * The single route table that drives prerendering, the sitemap, and
 * robots.txt. Adding a public page means adding a row here — nothing
 * else in the build needs to know about it.
 *
 * Kept in sync with the router in `src/App.tsx`; `routeManifest.test.ts`
 * fails if the two drift apart.
 */

/** Public pages: prerendered to static HTML, listed in the sitemap. */
export interface PublicRoute {
  path: string;
  /** ≤60 chars. Rendered verbatim as <title>. */
  title: string;
  /** ≤155 chars. The concrete payoff, no clickbait. */
  description: string;
  /** Sitemap hint. Content pages will override this from the register. */
  changefreq: "weekly" | "monthly" | "yearly";
  priority: number;
  /**
   * ISO date, omitted unless a real one exists. Content pages get theirs
   * from the register's last-verified field; marketing routes have no
   * such record yet, and a git or filesystem date would be a fabricated
   * signal — mechanical rewrites would bump it without the page changing.
   */
  lastmod?: string;
}

/**
 * App pages: client-rendered, never prerendered, never in the sitemap.
 *
 * `crawl` is deliberately one mechanism per route (see docs 01 §5):
 * robots.txt Disallow stops the fetch but cannot stop a bare externally
 * linked URL from being indexed; a `noindex` meta needs the page to be
 * fetched to be read. Applying both means the crawler never reads the
 * noindex, so the two are mutually exclusive, not complementary.
 */
export interface AppRoute {
  /** The router pattern, e.g. "/unsubscribe/:token". */
  pattern: string;
  /** The robots.txt / rewrite prefix, e.g. "/unsubscribe". */
  prefix: string;
  crawl: "disallow" | "noindex";
  reason: string;
}

export const PUBLIC_ROUTES: readonly PublicRoute[] = [
  {
    path: "/",
    title: "Appeal a Denied Insurance Claim | Revetra",
    description:
      "Upload your denial letter. See whether the policy your insurer cited actually applies, and how likely an appeal is to win. Free to look.",
    changefreq: "weekly",
    priority: 1.0,
  },
  {
    path: "/how-it-works",
    title: "How Revetra Works: Denial to Appeal Letter | Revetra",
    description:
      "Three steps, about twenty minutes: upload the denial, get the analysis, download an appeal letter you can send. Here is what happens at each stage.",
    changefreq: "monthly",
    priority: 0.8,
  },
  {
    path: "/pricing",
    title: "Pricing: Free Denial Analysis, One-Time Fee | Revetra",
    description:
      "The denial analysis is free. You only pay if you decide the appeal is worth filing, and it is a single one-time charge — no subscription.",
    changefreq: "monthly",
    priority: 0.8,
  },
  {
    path: "/faq",
    title: "Insurance Appeal Questions, Answered | Revetra",
    description:
      "Quick answers to what most people ask before starting an appeal: what Revetra does, what it costs, what happens to your documents.",
    changefreq: "monthly",
    priority: 0.7,
  },
  {
    path: "/help",
    title: "Help Center: Using Revetra | Revetra",
    description:
      "Short answers to the most common questions while you are working through an appeal — uploads, drafts, exports, and recovering your letter.",
    changefreq: "monthly",
    priority: 0.6,
  },
  {
    path: "/security",
    title: "Security: How Your Data Is Protected | Revetra",
    description:
      "The engineering facts — what we encrypt, where data lives, who can reach it, and when it is deleted.",
    changefreq: "yearly",
    priority: 0.4,
  },
  {
    path: "/hipaa",
    title: "HIPAA Notice: Your Health Information | Revetra",
    description:
      "Encrypted in transit and at rest, never used to train AI, and automatically deleted within 24 hours. The full notice.",
    changefreq: "yearly",
    priority: 0.4,
  },
  {
    path: "/privacy",
    title: "Privacy Policy | Revetra",
    description:
      "What we collect, why we collect it, and how long we keep it — in plain language, with the retention windows stated explicitly.",
    changefreq: "yearly",
    priority: 0.3,
  },
  {
    path: "/terms",
    title: "Terms of Service | Revetra",
    description:
      "Plain-language terms covering what Revetra does, what it does not do, and the limits of the service.",
    changefreq: "yearly",
    priority: 0.3,
  },
] as const;

export const APP_ROUTES: readonly AppRoute[] = [
  {
    pattern: "/session",
    prefix: "/session",
    crawl: "disallow",
    reason: "PHI-adjacent — uploaded denial letters and medical records",
  },
  {
    pattern: "/checkout/success",
    prefix: "/checkout",
    crawl: "disallow",
    reason: "PHI-adjacent — post-payment session state",
  },
  {
    pattern: "/checkout/cancel",
    prefix: "/checkout",
    crawl: "disallow",
    reason: "PHI-adjacent — post-payment session state",
  },
  {
    pattern: "/restore",
    prefix: "/restore",
    crawl: "disallow",
    reason: "PHI-adjacent — restores a session by identifier",
  },
  {
    pattern: "/delivery/recover",
    prefix: "/delivery",
    crawl: "disallow",
    reason: "PHI-adjacent — recovers a paid appeal letter",
  },
  {
    pattern: "/report-outcome/:token",
    prefix: "/report-outcome",
    crawl: "disallow",
    reason:
      "capability URL — the token in the path IS the credential, so a crawler fetching it exercises it",
  },
  {
    pattern: "/unsubscribe/:token",
    prefix: "/unsubscribe",
    crawl: "disallow",
    reason:
      "capability URL — the token in the path IS the credential, so a crawler fetching it exercises it",
  },
] as const;

/**
 * True for any URL on the app surface — the pages that handle uploaded
 * denial letters, payment state, and the two token-bearing routes.
 *
 * Lives here rather than in either caller because two copies of this
 * predicate is exactly how a new app route ends up classified as public
 * by one of them. The head-management hook and the analytics page-view
 * guard both read this one.
 */
export function isAppRoute(pathname: string): boolean {
  return APP_ROUTES.some(
    (route) => pathname === route.prefix || pathname.startsWith(`${route.prefix}/`),
  );
}

/** Distinct robots.txt / rewrite prefixes, in manifest order. */
export function appRoutePrefixes(): string[] {
  return [...new Set(APP_ROUTES.map((route) => route.prefix))];
}
